import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Switch,
  VersionTimeline,
  cn,
  type VersionSourceTone,
} from "@nexus/ui";
import {
  splitVersionChangeLabel,
  VERSION_CHANGE_LABEL_SEPARATOR,
} from "@nexus/contracts";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { formatCompactLocalDateTime } from "@/features/opportunities/lib/format-relative-time.js";
import type { PersonaBriefDto } from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import {
  personaBriefVersionHistoryQueryOptions,
  personaBriefVersionQueryOptions,
  useRestorePersonaBriefVersion,
} from "@/lib/queries/persona-opportunity-briefs.queries";

import type { PersonaBriefFieldChanges } from "./PersonaBriefExpandedBody.js";
import {
  briefFieldBlameByKey,
  briefHookFieldKey,
} from "./persona-brief-version-highlight.js";
import { PersonaScriptFieldDiff } from "./PersonaScriptFieldDiff.js";
import {
  CollapsedVersionsRail,
  OPEN_RAIL_CLASS,
  OPEN_RAIL_CONTENT_CLASS,
  VersionsRailCollapseButton,
  useVersionsRail,
  versionsRailGridClass,
} from "./VersionsRail.js";

const SOURCE_I18N: Record<string, { key: string; fallback: string }> = {
  GENERATED: {
    key: "personaOpportunities.scripts.history.sourceGenerated",
    fallback: "Generated",
  },
  MANUAL: {
    key: "personaOpportunities.scripts.history.sourceManual",
    fallback: "Manual",
  },
  AI_EDIT: {
    key: "personaOpportunities.scripts.history.sourceAiEdit",
    fallback: "AI edit",
  },
  RESTORED: {
    key: "personaOpportunities.scripts.history.sourceRestored",
    fallback: "Restored",
  },
};

const SOURCE_TONE: Record<string, VersionSourceTone> = {
  GENERATED: "generated",
  MANUAL: "manual",
  AI_EDIT: "ai",
  RESTORED: "restored",
};

const CHANGE_LABEL_I18N: Record<string, { key: string; fallback: string }> = {
  Title: {
    key: "personaOpportunities.scripts.history.title",
    fallback: "Title",
  },
  Duration: {
    key: "personaOpportunities.scripts.briefs.duration",
    fallback: "Duration",
  },
  "Age range": {
    key: "personaOpportunities.scripts.briefs.ageRange",
    fallback: "Age range",
  },
  Gender: {
    key: "personaOpportunities.scripts.briefs.gender",
    fallback: "Gender",
  },
  "Hook package": {
    key: "personaOpportunities.scripts.briefs.hookPackage",
    fallback: "Hook package",
  },
  "Selected hook": {
    key: "personaOpportunities.scripts.briefs.history.selectedHook",
    fallback: "Selected hook",
  },
  "Concept direction": {
    key: "personaOpportunities.scripts.briefs.history.conceptDirection",
    fallback: "Concept direction",
  },
  "Concept line": {
    key: "personaOpportunities.scripts.briefs.history.conceptLine",
    fallback: "Concept line",
  },
  "CTA direction": {
    key: "personaOpportunities.scripts.briefs.history.ctaDirection",
    fallback: "CTA direction",
  },
  "CTA line": {
    key: "personaOpportunities.scripts.briefs.history.ctaLine",
    fallback: "CTA line",
  },
  Notes: {
    key: "personaOpportunities.scripts.briefs.history.notes",
    fallback: "Notes",
  },
  Preferences: {
    key: "personaOpportunities.scripts.briefs.preferences",
    fallback: "Preferences",
  },
  "Product link": {
    key: "personaOpportunities.scripts.briefs.productLink",
    fallback: "Product link",
  },
  Font: { key: "personaOpportunities.scripts.briefs.font", fallback: "Font" },
  Logo: { key: "personaOpportunities.scripts.briefs.logo", fallback: "Logo" },
  Disclaimer: {
    key: "personaOpportunities.scripts.briefs.disclaimer",
    fallback: "Disclaimer",
  },
  "Brand reset": {
    key: "personaOpportunities.scripts.briefs.history.brandReset",
    fallback: "Brand reset",
  },
  "AI edit": {
    key: "personaOpportunities.scripts.history.sourceAiEdit",
    fallback: "AI edit",
  },
  "AI edit · Concept direction": {
    key: "personaOpportunities.scripts.briefs.history.aiConceptDirection",
    fallback: "AI edit · Concept direction",
  },
  "AI edit · Concept line": {
    key: "personaOpportunities.scripts.briefs.history.aiConceptLine",
    fallback: "AI edit · Concept line",
  },
  "AI edit · CTA direction": {
    key: "personaOpportunities.scripts.briefs.history.aiCtaDirection",
    fallback: "AI edit · CTA direction",
  },
  "AI edit · CTA line": {
    key: "personaOpportunities.scripts.briefs.history.aiCtaLine",
    fallback: "AI edit · CTA line",
  },
  "AI edit · Notes": {
    key: "personaOpportunities.scripts.briefs.history.aiNotes",
    fallback: "AI edit · Notes",
  },
  "AI edit · Preferences": {
    key: "personaOpportunities.scripts.briefs.history.aiPreferences",
    fallback: "AI edit · Preferences",
  },
};

const MANUAL_HOOK_CHANGE_LABEL = /^Hook ([1-4]) · (direction|line)$/;
const AI_HOOK_CHANGE_LABEL = /^AI edit · Hook ([1-4]) · (Direction|Line)$/;
const RESTORED_CHANGE_LABEL = /^Restored from v([1-9]\d*)$/;

function translateBriefChangeLabel(
  label: string | null,
  t: TFunction,
): string | null {
  const parts = splitVersionChangeLabel(label);
  if (parts.length === 0) return null;
  return parts
    .map((part) => translateBriefChangeLabelPart(part, t))
    .join(VERSION_CHANGE_LABEL_SEPARATOR);
}

function translateBriefChangeLabelPart(label: string, t: TFunction): string {
  const fixedCopy = CHANGE_LABEL_I18N[label];
  if (fixedCopy) return t(fixedCopy.key, fixedCopy.fallback);

  const manualHook = MANUAL_HOOK_CHANGE_LABEL.exec(label);
  if (manualHook) {
    const index = manualHook[1];
    return manualHook[2] === "direction"
      ? t("personaOpportunities.scripts.briefs.history.hookDirection", {
          defaultValue: "Hook {{index}} direction",
          index,
        })
      : t("personaOpportunities.scripts.briefs.history.hookLine", {
          defaultValue: "Hook {{index}} line",
          index,
        });
  }

  const aiHook = AI_HOOK_CHANGE_LABEL.exec(label);
  if (aiHook) {
    const index = aiHook[1];
    return aiHook[2] === "Direction"
      ? t("personaOpportunities.scripts.briefs.history.aiHookDirection", {
          defaultValue: "AI edit · Hook {{index}} · Direction",
          index,
        })
      : t("personaOpportunities.scripts.briefs.history.aiHookLine", {
          defaultValue: "AI edit · Hook {{index}} · Line",
          index,
        });
  }

  const restored = RESTORED_CHANGE_LABEL.exec(label);
  if (restored) {
    return t("personaOpportunities.scripts.briefs.history.restored", {
      defaultValue: "Restored version {{version}}",
      version: restored[1],
    });
  }

  return t(
    "personaOpportunities.scripts.briefs.history.unknownChange",
    "Updated",
  );
}

type PreviewGetter = (brief: PersonaBriefDto) => string;

type PreviewField = {
  key: string;
  labelKey: string;
  fallback: string;
  value: PreviewGetter;
};

export type BriefHistorySurface = "card" | "drawer";

export interface BriefHistoryView {
  isLatest: boolean;
  isShowingChanges: boolean;
  preview: PersonaBriefDto | undefined;
  /** Loaded while Show differences is on, so the document can mark changes in place. */
  changes: PersonaBriefFieldChanges | null;
  selectedVersion: number;
  versionCount: number;
  title: string;
  duration: string | undefined;
}

export interface BriefsHistoryPanelProps {
  enabled: boolean;
  setId: string;
  tenantId: string;
  brief: PersonaBriefDto;
  canRestore: boolean;
  surface?: BriefHistorySurface;
  /** Hub drawer omits the italic duration under "Viewing" on the current version. */
  hideCurrentDuration?: boolean;
  /** Sheet chrome above the two-column body. */
  header?: (view: BriefHistoryView) => ReactNode;
  /** Lifts the selection for chrome outside the panel; `null` follows the latest version. */
  selection?: {
    version: number | null;
    onChange: (version: number | null) => void;
  };
  /**
   * Hub embed. `fieldsPreview` is the older version or the diff, and is null on the latest
   * version in flat mode; the embed renders it where its own editable fields go.
   */
  leading?: (view: BriefHistoryView, fieldsPreview: ReactNode) => ReactNode;
}

export interface BriefsHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  brief: PersonaBriefDto;
  canRestore: boolean;
}

export function BriefsHistoryPanel({
  enabled,
  setId,
  tenantId,
  brief,
  canRestore,
  surface = "card",
  hideCurrentDuration = false,
  header,
  selection,
  leading,
}: BriefsHistoryPanelProps) {
  const { t } = useTranslation();
  /** `null` follows the latest version, so a save or restore lands on the new one. */
  const [localSelectedVersion, setLocalSelectedVersion] = useState<
    number | null
  >(null);
  const selectedVersion = selection ? selection.version : localSelectedVersion;
  const setSelectedVersion = selection
    ? selection.onChange
    : setLocalSelectedVersion;
  /** Version whose card has "Show differences" on; a new latest version lands flat. */
  const [diffVersion, setDiffVersion] = useState<number | null>(null);
  const restore = useRestorePersonaBriefVersion();
  const restoreInFlight = useRef(false);

  const historyQuery = useQuery(
    personaBriefVersionHistoryQueryOptions(tenantId, setId, brief.id, {
      enabled,
    }),
  );
  const versions = historyQuery.data?.versions ?? [];
  // The saved brief lands in the cache before the history refetch does.
  const latestVersion = Math.max(
    historyQuery.data?.activeVersion ?? 0,
    brief.version,
  );
  const viewingVersion = selectedVersion ?? latestVersion;
  const isLatest = viewingVersion === latestVersion;

  useEffect(() => {
    if (!enabled) {
      setSelectedVersion(null);
      setDiffVersion(null);
    }
  }, [enabled, setSelectedVersion]);

  function selectVersion(version: number) {
    setSelectedVersion(version === latestVersion ? null : version);
    setDiffVersion(null);
  }

  const previewQuery = useQuery(
    personaBriefVersionQueryOptions(
      tenantId,
      setId,
      brief.id,
      viewingVersion > 0 ? viewingVersion : null,
      { enabled: enabled && viewingVersion > 0 },
    ),
  );
  const originalQuery = useQuery(
    personaBriefVersionQueryOptions(tenantId, setId, brief.id, 1, {
      enabled,
    }),
  );
  const previousVersion = viewingVersion > 1 ? viewingVersion - 1 : null;
  const previousQuery = useQuery(
    personaBriefVersionQueryOptions(
      tenantId,
      setId,
      brief.id,
      previousVersion,
      {
        enabled: enabled && previousVersion != null,
      },
    ),
  );

  const preview = previewQuery.isPlaceholderData
    ? undefined
    : previewQuery.data?.brief;
  const original = originalQuery.data?.brief;
  const previous =
    previousVersion != null &&
    !previewQuery.isPlaceholderData &&
    !previousQuery.isPlaceholderData
      ? previousQuery.data?.brief
      : undefined;
  const title = (preview ?? brief).videoName;
  const duration = preview?.duration;
  const selectedInstruction =
    versions.find((entry) => entry.version === viewingVersion)?.instruction ??
    null;
  const versionCount = versions.length || brief.version;
  const blameByKey = useMemo(
    () => briefFieldBlameByKey(versions, viewingVersion),
    [versions, viewingVersion],
  );

  const timelineVersions = useMemo(
    () =>
      versions.map((entry) => {
        return {
          id: entry.id ?? `v${entry.version}`,
          version: entry.version,
          createdAt: entry.createdAt,
          createdAtLabel: formatCompactLocalDateTime(
            entry.createdAt,
            Date.now(),
          ),
          createdBy:
            entry.createdBy === "creative_engine"
              ? t(
                  "personaOpportunities.scripts.history.creativeEngine",
                  "Creative engine",
                )
              : entry.createdBy,
          sourceBadge: SOURCE_I18N[entry.source]
            ? t(
                SOURCE_I18N[entry.source].key,
                SOURCE_I18N[entry.source].fallback,
              )
            : entry.source,
          sourceTone: SOURCE_TONE[entry.source],
          changeLabel: translateBriefChangeLabel(entry.changeLabel, t),
          detail: entry.instruction,
          isCurrentState: entry.version === latestVersion,
        };
      }),
    [latestVersion, t, versions],
  );

  const activeVersionId =
    versions.find((entry) => entry.version === viewingVersion)?.id ??
    `v${viewingVersion}`;

  function handleRestore() {
    if (
      selectedVersion == null ||
      isLatest ||
      restore.isPending ||
      restoreInFlight.current
    ) {
      return;
    }
    restoreInFlight.current = true;
    restore.mutate(
      { setId, briefId: brief.id, version: selectedVersion, tenantId },
      {
        onSuccess: () => {
          setSelectedVersion(null);
        },
        onSettled: () => {
          restoreInFlight.current = false;
        },
      },
    );
  }

  const drawer = surface === "drawer";
  const rail = useVersionsRail(drawer, versionCount);
  const canShowChanges = previousVersion != null;
  const isShowingChanges = canShowChanges && diffVersion === viewingVersion;
  const view: BriefHistoryView = {
    isLatest,
    isShowingChanges,
    preview,
    changes:
      isShowingChanges && preview
        ? {
            previous,
            original,
            blameByKey,
            onSelectVersion: selectVersion,
          }
        : null,
    selectedVersion: viewingVersion,
    versionCount,
    title,
    duration,
  };
  const showPreview = leading == null || !isLatest || isShowingChanges;
  const fieldsPreview = !showPreview ? null : previewQuery.isError ? (
    <div className="flex flex-col items-start gap-2">
      <p
        role="alert"
        className={
          drawer
            ? "text-drawer-body text-muted-foreground"
            : "text-sm text-muted-foreground"
        }
      >
        {t(
          "personaOpportunities.scripts.briefs.history.previewLoadError",
          "Couldn't load this version.",
        )}
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => void previewQuery.refetch()}
      >
        {t("personaOpportunities.scripts.history.retry", "Retry")}
      </Button>
    </div>
  ) : !preview ? (
    <div
      className="flex flex-col gap-3"
      aria-label={t(
        "personaOpportunities.scripts.history.loadingPreview",
        "Loading preview…",
      )}
    >
      <Skeleton className="h-36 w-full rounded-sm" />
      <Skeleton className="h-36 w-full rounded-sm" />
    </div>
  ) : (
    <HistoryPreview
      surface={surface}
      brief={preview}
      original={isShowingChanges ? original : undefined}
      previous={isShowingChanges ? previous : undefined}
      blameByKey={blameByKey}
      onSelectVersion={selectVersion}
    />
  );
  const showDuration = Boolean(duration) && !(hideCurrentDuration && isLatest);
  const showDifferencesLabel = t(
    "personaOpportunities.scripts.history.showDifferences",
    "Show differences",
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header?.(view)}
      <div className={versionsRailGridClass(drawer, rail)}>
        <div
          className={cn(
            "min-h-0 overflow-y-auto px-6 py-5",
            !rail.isHidden &&
              "border-b border-border md:border-b-0 md:border-r",
          )}
        >
          <div className="mb-3.5 flex min-h-8 items-start justify-between gap-3.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p
                  className={
                    drawer
                      ? "text-drawer-body font-medium"
                      : "text-xs font-semibold"
                  }
                >
                  {t("personaOpportunities.scripts.history.viewing", {
                    defaultValue: "Viewing v{{version}}",
                    version: viewingVersion,
                  })}
                </p>
                {isLatest && !rail.isHidden ? (
                  <span
                    className={
                      drawer
                        ? "rounded-full bg-ai-subtle px-2 py-px text-caption font-medium text-ai-subtle-foreground"
                        : "rounded-full bg-ai-subtle px-2 py-px text-[10px] font-medium text-ai-subtle-foreground"
                    }
                  >
                    {t(
                      "personaOpportunities.scripts.history.current",
                      "Current",
                    )}
                  </span>
                ) : null}
              </div>
              {showDuration ? (
                <p className="mt-[3px] text-xs text-muted-foreground italic">
                  {duration}
                </p>
              ) : null}
              {isShowingChanges &&
              preview &&
              previous &&
              previewDiffersFromPrevious(preview, previous) ? (
                <p
                  className={
                    drawer
                      ? "mt-1 text-caption text-muted-foreground"
                      : "mt-1 text-[11px] text-muted-foreground"
                  }
                >
                  {t(
                    "personaOpportunities.scripts.history.diffLegend",
                    "Struck text was removed. Underlined text is the new wording.",
                  )}
                </p>
              ) : null}
              {selectedInstruction ? (
                <div className="mt-2.5">
                  <p
                    className={
                      drawer
                        ? "mb-1 text-caption font-medium tracking-label text-muted-foreground uppercase"
                        : "mb-1 font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase"
                    }
                  >
                    {t("personaOpportunities.scripts.history.prompt", "Prompt")}
                  </p>
                  <p
                    className={
                      drawer
                        ? "text-drawer-body leading-relaxed break-words whitespace-pre-wrap text-ai-text italic"
                        : "text-xs italic leading-relaxed break-words whitespace-pre-wrap text-ai-text"
                    }
                  >
                    {t("personaOpportunities.scripts.history.promptQuote", {
                      defaultValue: "“{{prompt}}”",
                      prompt: selectedInstruction,
                    })}
                  </p>
                </div>
              ) : null}
            </div>
            {canRestore && !isLatest && selectedVersion != null ? (
              <Button
                size="sm"
                iconStart="rotate-ccw"
                onClick={handleRestore}
                disabled={restore.isPending}
              >
                {t(
                  "personaOpportunities.scripts.history.useThisVersion",
                  "Use this version",
                )}
              </Button>
            ) : null}
          </div>

          {leading ? leading(view, fieldsPreview) : fieldsPreview}
        </div>

        {rail.isHidden ? null : rail.isCollapsed ? (
          <CollapsedVersionsRail
            rail={rail}
            versions={timelineVersions}
            activeVersionId={activeVersionId}
            onSelect={selectVersion}
          />
        ) : (
          <div
            id={rail.id}
            className={cn(
              "flex min-h-0 min-w-0 flex-col overflow-hidden",
              drawer && OPEN_RAIL_CLASS,
            )}
          >
            <div
              className={cn(
                "flex shrink-0 items-start justify-between gap-2 pt-4 pb-2",
                drawer ? cn("px-3", OPEN_RAIL_CONTENT_CLASS) : "px-[18px]",
              )}
            >
              <div className="min-w-0">
                <p
                  className={
                    drawer
                      ? "text-caption font-medium tracking-label text-muted-foreground uppercase"
                      : "font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase"
                  }
                >
                  {t(
                    "personaOpportunities.scripts.history.versions",
                    "Versions",
                  )}
                </p>
                <p
                  className={
                    drawer
                      ? "mt-1 text-caption leading-snug text-muted-foreground"
                      : "mt-1 text-[11px] leading-snug text-muted-foreground"
                  }
                >
                  {t(
                    "personaOpportunities.scripts.history.selectHint",
                    "Select one to preview it",
                  )}
                </p>
              </div>
              {drawer ? <VersionsRailCollapseButton rail={rail} /> : null}
            </div>
            <div
              className={cn(
                "min-h-0 flex-1 overflow-x-hidden overflow-y-auto",
                drawer
                  ? cn("px-3 pb-3", OPEN_RAIL_CONTENT_CLASS)
                  : "px-[18px] pb-[18px]",
              )}
            >
              {historyQuery.isLoading ? (
                <div
                  className="flex flex-col gap-2"
                  aria-label={t(
                    "personaOpportunities.scripts.history.loadingVersions",
                    "Loading versions…",
                  )}
                >
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : historyQuery.isError ? (
                <div className="flex flex-col items-start gap-2">
                  <p
                    className={
                      drawer
                        ? "text-drawer-body text-muted-foreground"
                        : "text-sm text-muted-foreground"
                    }
                  >
                    {t(
                      "personaOpportunities.scripts.history.loadError",
                      "Couldn't load version history.",
                    )}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void historyQuery.refetch()}
                  >
                    {t("personaOpportunities.scripts.history.retry", "Retry")}
                  </Button>
                </div>
              ) : (
                <VersionTimeline
                  variant="rail"
                  versions={timelineVersions}
                  activeVersionId={activeVersionId}
                  onSelect={(entry) => selectVersion(entry.version)}
                  labels={{
                    latest: t(
                      "personaOpportunities.scripts.history.latest",
                      "Latest",
                    ),
                    empty: t(
                      "personaOpportunities.scripts.history.empty",
                      "No versions yet.",
                    ),
                    select: t(
                      "personaOpportunities.scripts.history.select",
                      "Select version",
                    ),
                  }}
                  activeFooter={
                    canShowChanges ? (
                      <label
                        className={cn(
                          "flex min-h-11 min-w-0 cursor-pointer flex-wrap items-center gap-2 text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:min-h-7",
                          drawer ? "text-caption" : "text-[11px]",
                        )}
                      >
                        <Switch
                          checked={isShowingChanges}
                          onCheckedChange={(checked) =>
                            setDiffVersion(checked ? viewingVersion : null)
                          }
                          aria-label={showDifferencesLabel}
                        />
                        <span>{showDifferencesLabel}</span>
                      </label>
                    ) : null
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function BriefsHistoryDrawer({
  open,
  onOpenChange,
  setId,
  tenantId,
  brief,
  canRestore,
}: BriefsHistoryDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[1200px] flex-col gap-0 p-0 sm:max-w-[1200px]"
      >
        <BriefsHistoryPanel
          enabled={open}
          setId={setId}
          tenantId={tenantId}
          brief={brief}
          canRestore={canRestore}
          header={(view) => <BriefsHistorySheetHeader view={view} />}
        />
      </SheetContent>
    </Sheet>
  );
}

function BriefsHistorySheetHeader({ view }: { view: BriefHistoryView }) {
  const { t } = useTranslation();
  return (
    <SheetHeader className="shrink-0 gap-0 px-6 py-[18px]">
      <div className="flex min-w-0 items-center gap-2 pr-8">
        <SheetTitle className="truncate text-[15px] font-semibold">
          {view.title}
        </SheetTitle>
        <Badge
          variant="default"
          className="rounded-full px-2 py-px text-[11px]"
        >
          {t("personaOpportunities.scripts.history.versionCount", {
            defaultValue_one: "{{count}} version",
            defaultValue_other: "{{count}} versions",
            count: view.versionCount,
          })}
        </Badge>
      </div>
      <SheetDescription className="mt-[3px] text-xs text-muted-foreground">
        {t("personaOpportunities.scripts.history.subtitle", "Version history")}
      </SheetDescription>
    </SheetHeader>
  );
}

function highlightedClass(isDifferent: boolean): string {
  return isDifferent
    ? "border-l-2 border-l-ai bg-ai-subtle"
    : "border-l-2 border-l-transparent bg-background";
}

function hookText(
  brief: PersonaBriefDto | undefined,
  hookIndex: number,
  field: "direction" | "line",
): string {
  const hook = brief?.hooks.find((row) => row.hookIndex === hookIndex);
  return hook?.[field] ?? "";
}

function brandText(
  brief: PersonaBriefDto,
  field: keyof PersonaBriefDto["brand"],
): string {
  return brief.brand[field] ?? "";
}

function previewFields(): PreviewField[] {
  const hooks: PreviewField[] = [1, 2, 3, 4].flatMap((index) => [
    {
      key: briefHookFieldKey(index, "direction"),
      labelKey: "personaOpportunities.scripts.briefs.history.hookDirection",
      fallback: "Hook {{index}} direction",
      value: (brief) => hookText(brief, index, "direction"),
    },
    {
      key: briefHookFieldKey(index, "line"),
      labelKey: "personaOpportunities.scripts.briefs.history.hookLine",
      fallback: "Hook {{index}} line",
      value: (brief) => hookText(brief, index, "line"),
    },
  ]);

  return [
    {
      key: "duration",
      labelKey: "personaOpportunities.scripts.briefs.duration",
      fallback: "Duration",
      value: (brief) => brief.duration,
    },
    {
      key: "hookPackage",
      labelKey: "personaOpportunities.scripts.briefs.hookPackage",
      fallback: "Hook package",
      value: (brief) => String(brief.hookPackage),
    },
    {
      key: "selectedHookIndex",
      labelKey: "personaOpportunities.scripts.briefs.history.selectedHook",
      fallback: "Selected hook",
      value: (brief) => String(brief.selectedHookIndex),
    },
    {
      key: "ageRange",
      labelKey: "personaOpportunities.scripts.briefs.ageRange",
      fallback: "Age range",
      value: (brief) => brief.creatorProfile.ageRange,
    },
    {
      key: "gender",
      labelKey: "personaOpportunities.scripts.briefs.gender",
      fallback: "Gender",
      value: (brief) => brief.creatorProfile.gender,
    },
    ...hooks,
    {
      key: "conceptDirection",
      labelKey: "personaOpportunities.scripts.briefs.history.conceptDirection",
      fallback: "Concept direction",
      value: (brief) => brief.concept.direction,
    },
    {
      key: "conceptLine",
      labelKey: "personaOpportunities.scripts.briefs.history.conceptLine",
      fallback: "Concept line",
      value: (brief) => brief.concept.line,
    },
    {
      key: "ctaDirection",
      labelKey: "personaOpportunities.scripts.briefs.history.ctaDirection",
      fallback: "CTA direction",
      value: (brief) => brief.cta.direction,
    },
    {
      key: "ctaLine",
      labelKey: "personaOpportunities.scripts.briefs.history.ctaLine",
      fallback: "CTA line",
      value: (brief) => brief.cta.line,
    },
    {
      key: "preferences",
      labelKey: "personaOpportunities.scripts.briefs.preferences",
      fallback: "Preferences",
      value: (brief) => brief.creatorProfile.preferences ?? "",
    },
    {
      key: "notes",
      labelKey: "personaOpportunities.scripts.briefs.notesTitle",
      fallback: "Notes for the creator",
      value: (brief) => brief.notes ?? "",
    },
    {
      key: "productLink",
      labelKey: "personaOpportunities.scripts.briefs.productLink",
      fallback: "Product link",
      value: (brief) => brandText(brief, "productLink"),
    },
    {
      key: "font",
      labelKey: "personaOpportunities.scripts.briefs.font",
      fallback: "Font",
      value: (brief) => brandText(brief, "font"),
    },
    {
      key: "logo",
      labelKey: "personaOpportunities.scripts.briefs.logo",
      fallback: "Logo",
      value: (brief) => brandText(brief, "logo"),
    },
    {
      key: "disclaimer",
      labelKey: "personaOpportunities.scripts.briefs.disclaimer",
      fallback: "Disclaimer",
      value: (brief) => brandText(brief, "disclaimer"),
    },
  ];
}

function previewDiffersFromPrevious(
  preview: PersonaBriefDto,
  previous: PersonaBriefDto,
): boolean {
  return previewFields().some(
    (field) => field.value(preview) !== field.value(previous),
  );
}

function HistoryPreview({
  surface,
  brief,
  original,
  previous,
  blameByKey,
  onSelectVersion,
}: {
  surface: BriefHistorySurface;
  brief: PersonaBriefDto;
  original: PersonaBriefDto | undefined;
  previous: PersonaBriefDto | undefined;
  blameByKey: Map<string, number>;
  onSelectVersion: (version: number) => void;
}) {
  const { t } = useTranslation();
  const empty = t("personaOpportunities.scripts.history.emDash", "—");
  const fields = previewFields();
  const drawer = surface === "drawer";

  return (
    <dl className="overflow-hidden rounded-sm border border-border">
      {fields.map((field, index) => {
        const after = field.value(brief);
        const before = original ? field.value(original) : after;
        const isDifferent = original != null && after !== before;
        const previousValue = previous ? field.value(previous) : undefined;
        const hookIndex = field.key.startsWith("hook-")
          ? Number(field.key.split("-")[1])
          : undefined;
        const blameVersion = isDifferent
          ? (blameByKey.get(field.key) ?? null)
          : null;

        return (
          <div
            key={field.key}
            className={cn(
              "px-3 py-[9px]",
              index < fields.length - 1 && "border-b border-border",
              highlightedClass(isDifferent),
            )}
          >
            <dt className="mb-1 flex items-center justify-between gap-2">
              <span
                className={
                  drawer
                    ? "text-caption font-medium tracking-label text-muted-foreground uppercase"
                    : "font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase"
                }
              >
                {t(field.labelKey, {
                  defaultValue: field.fallback,
                  index: hookIndex,
                })}
              </span>
              {blameVersion != null ? (
                <button
                  type="button"
                  onClick={() => onSelectVersion(blameVersion)}
                  className={
                    drawer
                      ? "shrink-0 cursor-pointer text-caption font-medium tabular-nums text-ai-text transition-colors duration-150 ease-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                      : "shrink-0 cursor-pointer font-mono text-[10px] font-medium tabular-nums tracking-normal text-ai-text transition-colors duration-150 ease-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                  }
                  aria-label={t(
                    "personaOpportunities.scripts.history.changedInVersion",
                    {
                      defaultValue: "Changed in v{{version}}",
                      version: blameVersion,
                    },
                  )}
                >
                  {t("personaOpportunities.scripts.history.versionShort", {
                    defaultValue: "v{{version}}",
                    version: blameVersion,
                  })}
                </button>
              ) : null}
            </dt>
            <dd
              className={
                drawer
                  ? "text-drawer-body whitespace-pre-wrap text-foreground"
                  : "text-xs leading-[1.55] whitespace-pre-wrap text-foreground"
              }
            >
              <PersonaScriptFieldDiff
                before={previousValue}
                after={after}
                empty={empty}
              />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
